import { Box, Typography } from "@mui/material";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import React from "react";
import { useTranslations } from "next-intl";
import { ItineraryHeader } from "@/types/dto/pdp/pdp-itinerary";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import ItinerarySummarySkeleton from "./itinerary-summary-skeleton";
import ItineraryMapImage from "./itinerary-map-image";
import ItinerarySummaryRow from "./itinerary-summary-row";

interface ItinerarySummaryProps {
  itineraryHeader?: ItineraryHeader;
  handleOpenShare: () => void;
  handleOpenPrint: () => void;
  canViewPrint: boolean;
  handleOpenBestTimeToTravelIsOpen: () => void;
  hasBestTimeToTravel: boolean;
  isLoading?: boolean;
  shareButtonRef?: React.RefObject<HTMLDivElement | null>;
}

// Figma 11528:22915: action labels are 12px SemiBold under a 24px icon
const actionLabelSx = { fontSize: "1.2rem", fontWeight: 600, lineHeight: 1.3 };

const ItinerarySummary = ({
  itineraryHeader,
  handleOpenShare,
  handleOpenPrint,
  canViewPrint,
  handleOpenBestTimeToTravelIsOpen,
  hasBestTimeToTravel,
  isLoading,
  shareButtonRef
}: ItinerarySummaryProps) => {
  const t = useTranslations("PDP");

  if (isLoading) {
    return <ItinerarySummarySkeleton />;
  }
  return (
    <Box
      data-testid="itinerary-summary-container"
      id="itinerary"
      className="anchor-section"
      sx={{
        display: "flex",
        padding: { xs: "2.4rem 0rem", sm: "4rem 0rem", md: "4rem 0rem" },
        gap: "3.2rem",
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "column", md: "row" }
      }}
      data-track="visibility"
      data-parent-container-type="pdp"
      data-module-name="Itinerary Summary"
      data-ranking-algorithm="manual"
      data-module-ranking-position="7"
      component="section"
    >
      {/* Header */}
      <Box
        data-testid="itinerary-content-section"
        sx={{
          display: "flex",
          gap: { xs: "2rem", md: "2.4rem" },
          flex: 1,
          alignItems: "flex-start",
          flexDirection: "column"
        }}
      >
        <Box
          data-testid="itinerary-header"
          sx={{
            display: "flex",
            gap: "2rem",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "flex-start"
          }}
        >
          <Typography
            variant="h3"
            sx={{ fontSize: { xs: "2.4rem", sm: "2.6rem", md: "2.8rem" } }}
            component="h2"
            data-testid="itinerary-title"
          >
            {t("Itinerary")}
          </Typography>
          <Box
            data-testid="itinerary-actions"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1.8rem",
              alignItems: "center"
            }}
          >
            {canViewPrint && (
              <Box
                tabIndex={0}
                role="button"
                aria-label={t("View / Print")}
                data-testid="itinerary-print-button"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.4rem",
                  cursor: "pointer",
                  "&:focus-visible": {
                    outlineColor: "primary.main",
                    outline: "solid 1px",
                    outlineOffset: "1px"
                  }
                }}
                onClick={handleOpenPrint}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenPrint();
                  }
                }}
              >
                <LocalPrintshopOutlinedIcon sx={{ height: "24px", width: "24px" }} />
                <Typography data-testid="itinerary-print-button-label" sx={actionLabelSx}>
                  {t("View / Print")}
                </Typography>
              </Box>
            )}
            <Box
              ref={shareButtonRef}
              tabIndex={0}
              data-testid="itinerary-share-button"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.4rem",
                cursor: "pointer",
                "&:focus-visible": {
                  outlineColor: "primary.main",
                  outline: "solid 1px",
                  outlineOffset: "1px"
                }
              }}
              onClick={handleOpenShare}
            >
              <IosShareOutlinedIcon sx={{ height: "24px", width: "24px" }} />
              <Typography data-testid="itinerary-share-button-label" sx={actionLabelSx}>
                {t("Share")}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          data-testid="itinerary-details"
          sx={{
            display: "flex",
            width: { xs: "100%", md: "auto" },
            flexDirection: "column",
            gap: { xs: "4rem", sm: "2.4rem", md: "4rem" }
          }}
        >
          {/* Summary Row */}
          <ItinerarySummaryRow
            days={itineraryHeader?.days}
            nights={itineraryHeader?.nights}
            meals={itineraryHeader?.meals}
          />

          {/* Date Info */}
          <Box
            data-testid="itinerary-date-info-section"
            sx={{
              display: "flex",
              gap: "12px",
              flexDirection: { xs: "column", sm: "row", md: "column" }
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center" }}
              data-testid="itinerary-selected-date-info"
            >
              <Typography>
                <span style={{ fontWeight: "bold" }} data-testid="itinerary-selected-date-label">
                  {t("Selected Date")}
                </span>
                <span data-testid="itinerary-selected-date">{itineraryHeader?.selectedDate}</span>
              </Typography>
            </Box>
            <ColletteButton
              component="a"
              testId="itinerary-change-date-button"
              onClick={() => {
                const element = document.getElementById("dates-and-pricing-section");
                if (element) {
                  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                  const offsetPosition = elementPosition - 180;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth"
                  });
                }
              }}
              label={t("Change Date")}
              variant="outlined"
              size="large"
              sx={{
                alignSelf: "start"
              }}
            />
          </Box>

          {/* Best time to travel */}
          {hasBestTimeToTravel && (
            <Box
              data-testid="itinerary-best-time-to-travel"
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.5rem",
                alignItems: "center",
                width: "100%"
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "18px",
                  fontStyle: "normal",
                  fontWeight: "500",
                  lineHeight: "150%"
                }}
              >
                {t("Looking for the best time to travel")}
              </Typography>
              <Typography
                component="span"
                tabIndex={0}
                data-testid="itinerary-learn-more-link"
                onClick={handleOpenBestTimeToTravelIsOpen}
                sx={{
                  fontSize: "18px",
                  fontWeight: "700",
                  lineHeight: "150%",
                  textDecorationLine: "underline",
                  color: "secondary.main",
                  cursor: "pointer",
                  "&:focus-visible": {
                    borderColor: "primary.main",
                    border: "solid 1px"
                  }
                }}
              >
                {t("Learn More")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Map */}
      <ItineraryMapImage
        imgSrc={itineraryHeader?.mapImageUrl}
        imgAlt={itineraryHeader?.mapImageAltTag}
      />
    </Box>
  );
};

export default ItinerarySummary;
